import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import TambaLogo from "@/components/TambaLogo";
import { 
  CheckCircle2, 
  XCircle,
  Loader2,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | failed
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [user, setUser] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    const storedUser = localStorage.getItem("tamba_user");
    const userId = storedUser ? JSON.parse(storedUser).id : null;

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}${userId ? `?user_id=${userId}` : ''}`);
      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        
        // Update user subscription status in localStorage
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          userData.subscription_status = "premium";
          userData.subscription_plan = response.data.metadata?.plan_id;
          localStorage.setItem("tamba_user", JSON.stringify(userData));
        }
        return;
      } else if (response.data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/50 shadow-xl">
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="h-10 w-10 animate-spin text-accent" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">Processing Payment</h1>
                <p className="text-muted-foreground">
                  Please wait while we confirm your payment...
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                  className="w-20 h-20 rounded-full bg-tamba-teal/20 flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircle2 className="h-10 w-10 text-tamba-teal" />
                </motion.div>
                <h1 className="font-heading text-2xl font-bold mb-2">Payment Successful!</h1>
                <p className="text-muted-foreground mb-6">
                  Welcome to Premium! Your subscription is now active.
                </p>
                
                <div className="bg-tamba-yellow/10 border border-tamba-yellow/30 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <TambaLogo className="h-8 w-auto" />
                  </div>
                  <p className="text-sm font-semibold text-tamba-yellow">
                    Premium Unlocked - {paymentData?.metadata?.plan_name || "Premium"} Plan
                  </p>
                </div>
                
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="w-full rounded-full bg-tamba-blue hover:bg-tamba-blue/90"
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </>
            )}

            {status === "failed" && (
              <>
                <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="h-10 w-10 text-destructive" />
                </div>
                <h1 className="font-heading text-2xl font-bold mb-2">Payment Failed</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't confirm your payment. Please try again or contact support.
                </p>
                
                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate("/pricing")}
                    className="w-full rounded-full"
                    data-testid="try-again-btn"
                  >
                    Try Again
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/dashboard")}
                    className="w-full rounded-full"
                    data-testid="go-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
